import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Output,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuthService } from '@core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { AuctionView } from 'src/app/articles-manager/interfaces/auction.interface';
import { BidElementComponent } from '../bid-element/bid-element.component';

const sliceStepDefault = 60;

@Component({
  selector: 'app-bid-box',
  templateUrl: './bid-box.component.html',
  styleUrls: ['./bid-box.component.scss'],
  standalone: true,
  imports: [TranslateModule, CommonModule, BidElementComponent],
})
export class BidBoxComponent {
  isHomepage = input(false);
  isLarge = input(false);
  memorabidAuctions = input<Array<AuctionView>>([]);
  marketplaceAuctions = input<Array<AuctionView>>([]);
  memorabidAuctionsCount = input(0);
  marketplaceAuctionsCount = input(0);
  startingAuctionNumber = input(30);
  showMoreText = input('IDS_SEE_ALL');
  showRemainingAuctionNumber = input(false);
  showMoreRoute = input<string[] | null>(null);
  idEvent = input(-1);
  slug = input<string>(null);
  isSearchPage = input(false);

  isMarketplaceBox = computed<boolean>(
    () => this.marketplaceAuctionsLength() > 0
  );
  isMemorabidBox = computed<boolean>(() => this.memorabidAuctionsLength() > 0);

  memorabidAuctionsLength = computed<number>(() => {
    return this.memorabidAuctionsCount() >= this.memorabidAuctions().length
      ? this.memorabidAuctionsCount()
      : this.memorabidAuctions().length;
  });
  marketplaceAuctionsLength = computed<number>(() =>
    this.marketplaceAuctionsCount() >= this.marketplaceAuctions().length
      ? this.marketplaceAuctionsCount()
      : this.marketplaceAuctions().length
  );

  showCardsNumber = computed<number>(() => {
    if (this.isHomepage && this.isMemorabidBox()) {
      return (
        this.startingAuctionNumber() +
        this.memorabidAuctions().filter((a) => a.evidenceLevel === 3).length
      );
    }
    if (this.isMemorabidBox()) {
      return this.memorabidAuctionsLength();
    }
    return this.marketplaceAuctionsLength();
  });

  @Output('showMoreRequested')
  showMoreRequested = new EventEmitter();

  public authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly languageService = inject(LanguageService);
  public auctionDownloadedService = inject(AuctionDownloadedService);

  constructor() {
    effect(() => {
      if (!this.isHomepage()) {
        if (this.auctionDownloadedService.memorabidAuctionSliceEnd === 0) {
          this.auctionDownloadedService.memorabidAuctionSliceEnd =
            this.startingAuctionNumber();
        }
        if (this.auctionDownloadedService.marketplaceAuctionSliceEnd === 0) {
          this.auctionDownloadedService.marketplaceAuctionSliceEnd =
            this.startingAuctionNumber();
        }
      }
    });
  }

  bidElementTrackBy(index: number, auction: AuctionView): number {
    return auction.idAuction;
  }

  onShowMoreClick(): void {
    if (this.showMoreRoute() != null) {
      this.goToShowMoreRoute();
    } else if (this.isMemorabidBox()) {
      this.auctionDownloadedService.memorabidAuctionSliceEnd +=
        sliceStepDefault;
      if (
        this.auctionDownloadedService.memorabidAuctionSliceEnd >
        this.memorabidAuctions.length
      ) {
        this.showMoreRequested.emit();
      }
    } else {
      this.auctionDownloadedService.marketplaceAuctionSliceEnd +=
        sliceStepDefault;
      if (
        this.auctionDownloadedService.marketplaceAuctionSliceEnd >
        this.marketplaceAuctions.length
      ) {
        this.showMoreRequested.emit();
      }
    }
  }

  private goToShowMoreRoute(): void {
    let state: { fromMarketplaceButton?: boolean } = {};
    if (this.isMarketplaceBox()) {
      state.fromMarketplaceButton = true;
    }
    this._router.navigate(
      [this.languageService.userSelectedLanguage.code + this.showMoreRoute()],
      { state }
    );
  }
}
